import type { JourneyEdge } from "@starsach/shared-types";
import { readableLabel } from "./journeys";

/**
 * ADR-0012 phase 1: the step view, computed on read from the block-level
 * rollup. Plumbing blocks are contracted out, a retry loop becomes a count
 * on the step it returns to, and a branch is named by where it leads.
 * Counts through a contracted block are apportioned by its outgoing
 * proportions, so they are approximate; phase 2 makes them exact in the
 * aggregator, where the sequences are.
 */

/** Blocks a caller never notices. Matched on the console name at the start of the label. */
const PASSTHROUGH =
  /^(set logging behavior|set voice|set contact attributes|update contact attributes|set event hook|set recording( and analytics)? behavior|set whisper flow|set hold flow|set customer queue flow|set disconnect flow|loop|wait|play prompt)\b/i;

/** Where a branch leads, when the target says: "Transfer to queue - Sales" names the branch Sales. */
const DESTINATION = /^(transfer to queue|set working queue|transfer to flow|transfer to phone number|transfer) - (.+)$/i;

export type StepEdge = JourneyEdge & {
  /** What the caller heard on the way, when a prompt was contracted out of this edge. */
  via?: string;
  /** The branch as a meaning ("Sales") rather than a key ("pressed 2"), when the destination names it. */
  meaning?: string;
};

export interface StepGraph {
  edges: StepEdge[];
  /** Retries folded into the step they return to: node id → contacts that came back. */
  retries: Map<string, number>;
  /** How many blocks were contracted out. */
  collapsed: number;
}

const isPassthrough = (label: string, nodeId: string) => PASSTHROUGH.test(readableLabel(label, nodeId));

export function toSteps(edges: JourneyEdge[]): StepGraph {
  let current: StepEdge[] = edges.map((e) => ({ ...e }));
  const retries = new Map<string, number>();
  let collapsed = 0;
  // A passthrough with no way out (the log ended there) is where calls ended: kept as a terminal, not contracted.
  const terminal = new Set<string>();

  // Contract one passthrough node at a time; each pass may expose another (Play prompt → Loop → …).
  for (;;) {
    const labels = new Map<string, string>();
    for (const e of current) {
      labels.set(e.from, e.fromLabel);
      labels.set(e.to, e.toLabel);
    }
    const node = [...labels].find(([id, label]) => !terminal.has(id) && isPassthrough(label, id))?.[0];
    if (!node) break;
    const into = current.filter((e) => e.to === node && e.from !== node);
    const outOf = current.filter((e) => e.from === node && e.to !== node);
    if (outOf.length === 0) {
      terminal.add(node);
      continue;
    }
    collapsed++;
    const rest = current.filter((e) => e.from !== node && e.to !== node);
    const outTotal = outOf.reduce((a, e) => a + e.count, 0);
    const heard = labels.get(node) ?? "";
    const prompt = /^play prompt - (.+)$/i.exec(readableLabel(heard, node))?.[1];
    const merged = new Map<string, StepEdge>();
    for (const a of into) {
      for (const b of outOf) {
        const share = outTotal ? b.count / outTotal : 1 / outOf.length;
        const count = a.count * share;
        const key = `${a.from}->${b.to}#${a.branch ?? ""}`;
        const cur = merged.get(key);
        const via = a.via ?? prompt ?? b.via;
        if (cur) {
          cur.count += count;
          cur.errorCount += b.errorCount * (a.count / Math.max(1, into.reduce((s, e) => s + e.count, 0)));
          continue;
        }
        merged.set(key, {
          ...a,
          to: b.to,
          toLabel: b.toLabel,
          count,
          errorCount: b.errorCount * (a.count / Math.max(1, into.reduce((s, e) => s + e.count, 0))),
          avgDwellMs: a.avgDwellMs === undefined && b.avgDwellMs === undefined ? undefined : (a.avgDwellMs ?? 0) + (b.avgDwellMs ?? 0),
          sampleContactIds: [...new Set([...(a.sampleContactIds ?? []), ...(b.sampleContactIds ?? [])])].slice(0, 20),
          ...(via ? { via } : {}),
        });
      }
    }
    current = [...rest, ...merged.values()];
  }

  // A step that leads back to itself is a retry: a number on the step, not an arrow.
  const out: StepEdge[] = [];
  for (const e of current) {
    if (e.from === e.to) {
      retries.set(e.from, (retries.get(e.from) ?? 0) + e.count);
      continue;
    }
    out.push(e);
  }

  // Merge edges that contraction made parallel, round the apportioned counts, name the branches.
  const byKey = new Map<string, StepEdge>();
  for (const e of out) {
    const key = `${e.from}->${e.to}#${e.branch ?? ""}`;
    const cur = byKey.get(key);
    if (cur) {
      cur.count += e.count;
      cur.errorCount += e.errorCount;
      cur.sampleContactIds = [...new Set([...(cur.sampleContactIds ?? []), ...(e.sampleContactIds ?? [])])].slice(0, 20);
      continue;
    }
    byKey.set(key, e);
  }
  const edgesOut = [...byKey.values()]
    .map((e) => {
      const meaning = branchMeaning(e);
      return { ...e, count: Math.round(e.count), errorCount: Math.round(e.errorCount), ...(meaning ? { meaning } : {}) };
    })
    .filter((e) => e.count > 0)
    .sort((a, b) => b.count - a.count);
  for (const [k, v] of retries) retries.set(k, Math.round(v));
  return { edges: edgesOut, retries, collapsed };
}

/** "pressed 2" becomes "Finance" when the edge lands on "Transfer to flow - Finance authentication". */
export function branchMeaning(edge: Pick<StepEdge, "branch" | "toLabel" | "to">): string | undefined {
  if (!edge.branch || !/^(\d{1,2}|[*#])$/.test(edge.branch)) return undefined;
  const m = DESTINATION.exec(readableLabel(edge.toLabel, edge.to));
  return m?.[2]?.replace(/…$/, "").trim() || undefined;
}
